/**
 * Shared timezone utilities — DST-aware using Intl API.
 * Maps globalTimezone values (from userPreferences) to IANA names.
 */

/** Map from globalTimezone value (stored in userPreferences) → IANA timezone name */
export const TIMEZONE_VALUE_TO_IANA: Record<string, string> = {
  '0':     'UTC',
  '-10':   'Pacific/Honolulu',
  '-9':    'America/Anchorage',
  '-9b':   'America/Juneau',
  '-8':    'America/Los_Angeles',
  '-8b':   'America/Vancouver',
  '-7':    'America/Denver',
  '-7b':   'America/Phoenix',
  '-6':    'America/Chicago',
  '-6b':   'America/El_Salvador',
  '-6c':   'America/Mexico_City',
  '-5':    'America/Bogota',
  '-5b':   'America/Lima',
  '-5c':   'America/New_York',
  '-5d':   'America/Toronto',
  '-4':    'America/Caracas',
  '-3':    'America/Argentina/Buenos_Aires',
  '-3b':   'America/Santiago',
  '-3c':   'America/Sao_Paulo',
  '-1':    'Atlantic/Azores',
  '0b':    'Europe/Dublin',
  '0c':    'Europe/Lisbon',
  '0d':    'Europe/London',
  '0e':    'Atlantic/Reykjavik',
  '1':     'Europe/Amsterdam',
  '1b':    'Europe/Belgrade',
  '1c':    'Europe/Berlin',
  '1d':    'Europe/Bratislava',
  '1e':    'Europe/Brussels',
  '1f':    'Europe/Budapest',
  '1g':    'Africa/Casablanca',
  '1h':    'Europe/Copenhagen',
  '1i':    'Africa/Lagos',
  '1j':    'Europe/Luxembourg',
  '1k':    'Europe/Madrid',
  '1l':    'Europe/Malta',
  '1m':    'Europe/Oslo',
  '1n':    'Europe/Paris',
  '1o':    'Europe/Prague',
  '1p':    'Europe/Rome',
  '1q':    'Europe/Stockholm',
  '1r':    'Africa/Tunis',
  '1s':    'Europe/Warsaw',
  '1t':    'Europe/Vienna',
  '1u':    'Europe/Zurich',
  '2':     'Europe/Athens',
  '2b':    'Europe/Bucharest',
  '2c':    'Europe/Helsinki',
  '2d':    'Asia/Jerusalem',
  '2e':    'Africa/Johannesburg',
  '2f':    'Africa/Cairo',
  '2g':    'Asia/Nicosia',
  '2h':    'Europe/Riga',
  '2i':    'Europe/Tallinn',
  '2j':    'Europe/Vilnius',
  '3':     'Asia/Bahrain',
  '3b':    'Europe/Istanbul',
  '3c':    'Asia/Kuwait',
  '3d':    'Europe/Moscow',
  '3e':    'Africa/Nairobi',
  '3f':    'Asia/Qatar',
  '3g':    'Asia/Riyadh',
  '3.5':   'Asia/Tehran',
  '4':     'Asia/Dubai',
  '4b':    'Asia/Muscat',
  '4.5':   'Asia/Kabul',
  '5':     'Asia/Ashgabat',
  '5b':    'Asia/Almaty',
  '5c':    'Asia/Karachi',
  '5.5':   'Asia/Kolkata',
  '5.5b':  'Asia/Colombo',
  '5.75':  'Asia/Kathmandu',
  '6':     'Asia/Dhaka',
  '6.5':   'Asia/Yangon',
  '7':     'Asia/Bangkok',
  '7b':    'Asia/Jakarta',
  '7c':    'Asia/Ho_Chi_Minh',
  '8':     'Asia/Chongqing',
  '8b':    'Asia/Hong_Kong',
  '8c':    'Asia/Kuala_Lumpur',
  '8d':    'Asia/Manila',
  '8e':    'Australia/Perth',
  '8f':    'Asia/Shanghai',
  '8g':    'Asia/Singapore',
  '8h':    'Asia/Taipei',
  '9':     'Asia/Seoul',
  '9b':    'Asia/Tokyo',
  '10':    'Australia/Brisbane',
  '10.5':  'Australia/Adelaide',
  '11':    'Australia/Sydney',
  '12':    'Pacific/Norfolk',
  '13':    'Pacific/Auckland',
  '13b':   'Pacific/Fakaofo',
  '13.75': 'Pacific/Chatham',
};

/** Map from globalTimezone value → city label (used for display in card header) */
export const TIMEZONE_VALUE_TO_CITY: Record<string, string> = {
  '0':     'UTC',
  '-10':   'Honolulu',
  '-9':    'Ancrage',
  '-9b':   'Juneau',
  '-8':    'Los Angeles',
  '-8b':   'Vancouver',
  '-7':    'Denver',
  '-7b':   'Phoenix',
  '-6':    'Chicago',
  '-6b':   'San Salvador',
  '-6c':   'Mexico City',
  '-5':    'Bogota',
  '-5b':   'Lima',
  '-5c':   'New York',
  '-5d':   'Toronto',
  '-4':    'Caracas',
  '-3':    'Buenos Aires',
  '-3b':   'Santiago',
  '-3c':   'São Paulo',
  '-1':    'Açores',
  '0b':    'Dublin',
  '0c':    'Lisbonne',
  '0d':    'Londres',
  '0e':    'Reykjavik',
  '1':     'Amsterdam',
  '1b':    'Belgrade',
  '1c':    'Berlin',
  '1d':    'Bratislava',
  '1e':    'Bruxelles',
  '1f':    'Budapest',
  '1g':    'Casablanca',
  '1h':    'Copenhague',
  '1i':    'Lagos',
  '1j':    'Luxembourg',
  '1k':    'Madrid',
  '1l':    'Malte',
  '1m':    'Oslo',
  '1n':    'Paris',
  '1o':    'Prague',
  '1p':    'Rome',
  '1q':    'Stockholm',
  '1r':    'Tunis',
  '1s':    'Varsovie',
  '1t':    'Vienne',
  '1u':    'Zurich',
  '2':     'Athènes',
  '2b':    'Bucarest',
  '2c':    'Helsinki',
  '2d':    'Jérusalem',
  '2e':    'Johannesburg',
  '2f':    'Le Caire',
  '2g':    'Nicosie',
  '2h':    'Riga',
  '2i':    'Tallinn',
  '2j':    'Vilnius',
  '3':     'Bahrein',
  '3b':    'Istanbul',
  '3c':    'Koweit',
  '3d':    'Moscou',
  '3e':    'Nairobi',
  '3f':    'Qatar',
  '3g':    'Riyadh',
  '3.5':   'Téhéran',
  '4':     'Dubaï',
  '4b':    'Muscat',
  '4.5':   'Kaboul',
  '5':     'Ashkhabad',
  '5b':    'Astana',
  '5c':    'Karachi',
  '5.5':   'Calcuta',
  '5.5b':  'Colombo',
  '5.75':  'Katmandou',
  '6':     'Dhaka',
  '6.5':   'Yangon',
  '7':     'Bangkok',
  '7b':    'Djakarta',
  '7c':    'Ho Chi Minh',
  '8':     'Chongqing',
  '8b':    'Hong Kong',
  '8c':    'Kuala Lumpur',
  '8d':    'Manille',
  '8e':    'Perth',
  '8f':    'Shanghai',
  '8g':    'Singapour',
  '8h':    'Taipei',
  '9':     'Séoul',
  '9b':    'Tokyo',
  '10':    'Brisbane',
  '10.5':  'Adélaïde',
  '11':    'Sydney',
  '12':    'Île Norfolk',
  '13':    'Nouvelle-Zélande',
  '13b':   'Tokelau',
  '13.75': 'Îles Chatham',
};

/** Label affiché sur les graphiques horaires — ex. "UTC+1 · Paris" */
export function resolveTraderTimezoneLabel(
  globalTimezone?: string,
  iana?: string,
): string {
  const resolvedIana = iana ?? getIanaFromGlobalTimezone(globalTimezone);
  const offsetHours = getUtcOffsetHoursAt(resolvedIana, new Date());
  const offset = formatUtcOffset(offsetHours);
  const utcLabel =
    offsetHours === 0 ? 'UTC' : offset.startsWith('-') ? `UTC${offset}` : `UTC+${offset.replace('+', '')}`;

  const city = globalTimezone ? TIMEZONE_VALUE_TO_CITY[globalTimezone] : undefined;
  if (city && city !== 'UTC') {
    return `${utcLabel} · ${city}`;
  }
  return utcLabel;
}

/** Contexte date/heure actuel dans le fuseau trader (pour Atlas journal). */
export function getTraderNowContext(globalTimezone?: string) {
  const iana = getIanaFromGlobalTimezone(globalTimezone);
  const now = new Date();
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: iana,
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const day = get('day');
  const month = get('month');
  const year = get('year');
  let hour = get('hour');
  if (hour === '24') hour = '00';
  const minute = get('minute');

  return {
    currentDate: `${year}-${month}-${day}`,
    currentDateTime: `${day}/${month}/${year} ${hour}:${minute}`,
    currentWeekday: get('weekday'),
    timezoneLabel: resolveTraderTimezoneLabel(globalTimezone),
  };
}

/** Returns the IANA timezone name from a globalTimezone preference value */
export function getIanaFromGlobalTimezone(value: string | undefined): string {
  if (!value) return 'UTC';
  return TIMEZONE_VALUE_TO_IANA[value] ?? 'UTC';
}

/** Returns the numeric UTC offset from a globalTimezone preference value.
 *  e.g. "1n" → 1, "-5c" → -5, "0" → 0. Falls back to 0 if undefined/invalid. */
export function getGlobalTimezoneOffset(globalTimezone?: string): number {
  if (!globalTimezone) return 0;
  const n = parseFloat(globalTimezone.replace(/[a-z]/gi, ''));
  return isNaN(n) ? 0 : n;
}

/** Returns the current UTC offset in fractional hours for a given IANA timezone at a specific date.
 *  Uses Intl API → correctly handles DST (summer/winter time). */
export function getUtcOffsetHoursAt(ianaName: string, date: Date): number {
  try {
    const parts = Intl.DateTimeFormat('en', {
      timeZone: ianaName,
      timeZoneName: 'shortOffset',
    }).formatToParts(date);
    const tzStr = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    if (tzStr === 'GMT') return 0;
    const match = tzStr.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (match) {
      const sign = match[1] === '+' ? 1 : -1;
      const h = parseInt(match[2], 10);
      const m = parseInt(match[3] ?? '0', 10);
      return sign * (h + m / 60);
    }
  } catch {}
  return 0;
}

/** Formats UTC offset hours as a string like "+2", "-4", "+5:30" */
export function formatUtcOffset(offsetHours: number): string {
  const sign = offsetHours >= 0 ? '+' : '-';
  const abs = Math.abs(offsetHours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return m > 0 ? `${sign}${h}:${String(m).padStart(2, '0')}` : `${sign}${h}`;
}

/** Formate une date UTC pour l'affichage (ex: "15/04/2026 08:13") dans le IANA cible, tout en respectant le DST. */
export function formatToLocalDisplayString(utcDate: Date, ianaName: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaName,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(utcDate);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
    let h = get('hour');
    if (h === '24') h = '00';
    return `${get('day')}/${get('month')}/${get('year')} ${h}:${get('minute')}`;
  } catch {
    return 'Date non disponible';
  }
}

/**
 * Converts a trade's openDateTime to an actual UTC Date.
 * openDateTime stores local time (broker or user) encoded as if UTC — 
 * the offset from userPreferences / account is required to recover real UTC.
 */
export function getTradeUtcDate(trade: any, account?: any): Date | null {
  if (!trade?.openDateTime) return null;

  try {
    let year: number, month: number, day: number, storedHours: number, storedMinutes: number;

    if (typeof trade.openDateTime === 'string') {
      const match = trade.openDateTime.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (!match) return null;
      year = parseInt(match[1]); month = parseInt(match[2]); day = parseInt(match[3]);
      storedHours = parseInt(match[4]); storedMinutes = parseInt(match[5]);
    } else if (
      typeof trade.openDateTime === 'object' &&
      trade.openDateTime !== null &&
      'seconds' in trade.openDateTime
    ) {
      const d = new Date(trade.openDateTime.seconds * 1000);
      year = d.getUTCFullYear(); month = d.getUTCMonth() + 1; day = d.getUTCDate();
      storedHours = d.getUTCHours(); storedMinutes = d.getUTCMinutes();
    } else if (trade.openDateTime instanceof Date) {
      year = trade.openDateTime.getUTCFullYear(); month = trade.openDateTime.getUTCMonth() + 1;
      day = trade.openDateTime.getUTCDate();
      storedHours = trade.openDateTime.getUTCHours(); storedMinutes = trade.openDateTime.getUTCMinutes();
    } else {
      return null;
    }

    // Determine the UTC offset that the stored time was expressed in
    const isAutoTrade = trade?.source === 'auto';
    let storedOffsetHours: number;
    if (isAutoTrade) {
      // originalTimeZone may be stored as number or string in Firestore
      const origTz = account?.originalTimeZone;
      storedOffsetHours = origTz !== undefined && origTz !== null
        ? (parseFloat(String(origTz)) || 0)
        : 0;
    } else {
      // targetUtc is injected by enrichedAccount (= numeric globalOffset)
      storedOffsetHours = account?.targetUtc !== undefined
        ? (parseFloat(String(account.targetUtc).replace(/[a-z]/gi, '')) || 0)
        : (account?.globalTimezone !== undefined
            ? (parseFloat(String(account.globalTimezone).replace(/[a-z]/gi, '')) || 0)
            : 0);
    }

    // UTC = storedLocalTime - storedOffset (in minutes for fractional offsets)
    const offsetMins = Math.round(storedOffsetHours * 60);
    const storedTotalMins = storedHours * 60 + storedMinutes;
    const utcTotalMins = storedTotalMins - offsetMins;

    const utcDate = new Date(Date.UTC(year, month - 1, day, 0, utcTotalMins));
    return isNaN(utcDate.getTime()) ? null : utcDate;
  } catch {
    return null;
  }
}
